import React, {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {ImageMediaItemViewModel, MediaItemViewModel, VideoMediaItemViewModel} from "../converter/ViewModel";
import {ApiImage, expandImageUrl} from "../ApiImage";
import {useEnvironment} from "@webng/react-app-common";
import {pendingVideoPreviews, VideoFrameOptions, videoFrameUrl} from "../converter/imageUrl";
import {safeLinkUrl} from "../converter/safeUrl";
import {Eye, Film, Frown} from "react-feather";
import {useTranslation} from 'react-i18next';
import classNames from "classnames";
import {useMediaExtraComponent} from "../LiveblogRenderContext";
import {IMedia} from "@webng-types/write-model";
import useDebouncedResizeObserver from "@webng/react-app-common/src/utils/useDebouncedResizeObserver";

export type OnMediaClickHandler = (media: IMedia) => void

function unknownKind(block: never): never {
  throw new Error("Unknown Media Item type " + block);
}


interface MediaWithContentWarningViewProps {
  className?: string,
  item: ImageMediaItemViewModel | VideoMediaItemViewModel
  children: ReactNode
}

function MediaWithContentWarningView({item, children}: MediaWithContentWarningViewProps) {
  const {t} = useTranslation('liveblog')
  const [liftContentWarning, setLiftContentWarning] = useState(false)

  const onLiftContentWarning = useCallback(() => {
    setLiftContentWarning(true)
  }, [])

  if(typeof item.contentWarning === 'string' && !liftContentWarning) {
    return <ImageMediaItemView item={item} warning={true}>
      <div className="tik4-media-image__warning">
        <div className="tik4-media-image__warning__content">
          <button className="tik4-media-image__warning__button tik4-button tik4-button--txt--l" onClick={onLiftContentWarning} type="button"><Eye className="tik4-icon tik4-icon--mr" />{t('liveblog.media.content_warning.button')}</button>
          <span className="tik4-media-image__warning__label">{item.contentWarning || t('liveblog.media.content_warning.default_label')}</span>
        </div>
        <div className="tik4-media-image__warning__overlay"></div>
      </div>
    </ImageMediaItemView>

  } else {
    return <React.Fragment>{children}</React.Fragment>
  }
}


interface MediaItemContainerProps {
  className?: string,
  item: ImageMediaItemViewModel | VideoMediaItemViewModel
  children: ReactNode
}
function MediaItemContainer({className, item, children }:MediaItemContainerProps) {
  const MediaExtraComponent = useMediaExtraComponent();

  const clsName = classNames('tik4-media-item', { relative: !!MediaExtraComponent} ,className);
  return <div className={clsName}>
    {children}

    {MediaExtraComponent && <MediaExtraComponent media={item.media} />}
  </div>
}

interface ImageMediaItemViewProps {
  item: ImageMediaItemViewModel | VideoMediaItemViewModel,
  warning?: boolean,
  children?: ReactNode
  onMediaClick?: OnMediaClickHandler
}

function ImageMediaItemView({item, warning, children, onMediaClick} : ImageMediaItemViewProps) {
  const maxPreview = item.previews[item.previews.length - 1];
  const srcSet = item.previews.map(p => `${p.url} ${p.width}w`).join(", ");
  const {ref, width} = useDebouncedResizeObserver(250);
  const {t} = useTranslation("liveblog");

  const onMediaItemClick = useMemo(() => {
    if(onMediaClick && item.media) {
      return () => {
        if(item.media) {
          onMediaClick(item.media)
        }
      }
    } else {
      return undefined
    }
  }, [onMediaClick, item.media])

  const lightboxActive = !!onMediaItemClick;
  // Validate the click-through at this href sink — link_url is stored unsanitized by every writer,
  // and React does not block a `javascript:` href (see safeLinkUrl).
  const href: string | undefined = safeLinkUrl((item as any).linkUrl) ?? undefined;
  const shouldWrapWithLink = !!href && !lightboxActive;

  const img = (
    <ApiImage
      className={"tik4-media-image__img"}
      sizes={`(max-width: 100vw) ${Math.min(width || 0, maxPreview.width)}px, 100vw`}
      srcSet={srcSet}
      alt={item.title}
      width={maxPreview.width}
      height={maxPreview.height}
    />
  );

  const sharedImgWrapperProps = {
    ref: ref,
    style: {
      "--t4-media-img-width": maxPreview.width,
      "--t4-media-img-height": maxPreview.height} as React.CSSProperties
  }

  return <MediaItemContainer className="tik4-media-item--image" item={item}>

    <div className={`tik4-media-image ${warning ? 'tik4-media-image--has-warning' : ''}`}>
      {shouldWrapWithLink && href ?
        <>
          <a
            {...sharedImgWrapperProps}
              href={href}
              target='_blank'
              rel='noopener'
              className="tik4-media-image__img-wrapper tik4-media-image__img-wrapper--link"
            >
            {img}
            {children}
          </a>
        </>
      :
       <>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events*/}
        <div
        {...sharedImgWrapperProps}
        className={`tik4-media-image__img-wrapper ${lightboxActive ? 'tik4-media-image__img-wrapper--slideshow-trigger' : ''}`} onClick={onMediaItemClick} role={lightboxActive ? 'button' : undefined} aria-label={lightboxActive ? t('liveblog.media.item.open') : undefined}>
          {img}
          {children}
        </div>
       </>
      }

        {/* TODO:Timo blur one day :D */}
        {/* <img
            className="tik4-media-image__blur"
            src={item.previews[0]}
            alt={item.title}
            title={item.title}
            width={item.originalWidth}
            height={item.originalHeight}
        /> */}
      <MediaItemTitleCredit item={item} />
    </div>

  </MediaItemContainer>
}


function UnfinishedVideoMediaItemView({item} : {item: VideoMediaItemViewModel}) {
  const {t} = useTranslation("liveblog");

  return <ImageMediaItemView item={item}>
    <div className="absolute z-10 left-0 top-0 right-0 bottom-0 flex items-center justify-center text-center overlay-bg-dark text-white flex-col">
      <Film />
      <span className="mt-2 text-xs">{t('liveblog.media.processing_video')}</span>
    </div>
  </ImageMediaItemView>
}

// The preview hands a third-party feed URL straight to <video src>. Only https may reach the browser: an
// http source would be mixed content in the editor (blocked or upgraded to a URL that may not exist), so
// it falls through to the placeholder even though the server-side download will handle it fine.
function isHttpsUrl(url: string | undefined): url is string {
  if(!url) return false
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

// Inline pending-video glyph — a data URI so this poster can never 404 in any environment
// (no iconpack/CDN dependency, unlike pendingVideoPreviews() elsewhere in this file).
const PENDING_VIDEO_POSTER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 96 96'%3E%3Cpath fill='%23F2F2F2' d='M69,28C69,25.8 67.2,24 65,24L17,24C14.8,24 13,25.8 13,28L13,68C13,70.2 14.8,72 17,72L65,72C67.2,72 69,70.2 69,68L69,28Z'/%3E%3Cpolygon fill='%23F2F2F2' points='66.5,48 69,54 85,70 85,26 69,42'/%3E%3C/svg%3E"

// A content-stream preview item whose media is still the feed's own video file: nothing has been
// transcoded yet, so play the source directly instead of the player frame (which needs a posted media).
function RawVideoMediaItemView({item, url} : {item: VideoMediaItemViewModel, url: string}) {
  const {t} = useTranslation("liveblog");

  const ratio = item.originalWidth && item.originalHeight ? item.originalHeight / item.originalWidth : 9 / 16;
  const cssHeight = `min(${ratio * 100}%, var(--t4-media-max-h))`

  return <MediaItemContainer className="tik4-media-item--video tik4-media-item--video-raw" item={item}>
    <div className="tik4-media-video">
      <div className="tik4-media-video__video-wrapper" style={{paddingTop: cssHeight, position: "relative", width: `calc(${cssHeight} * ${1/ratio})`, maxWidth: "100%"}}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- third-party feed file, no caption track exists */}
        <video
          src={url}
          poster={PENDING_VIDEO_POSTER}
          title={item.title || t('liveblog.media.video.fallback_title')}
          controls
          playsInline
          preload="metadata"
          width="100%"
          height="100%"
          style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: "#000"}}
        />
      </div>
      <MediaItemTitleCredit item={item} />
    </div>
  </MediaItemContainer>
}

function FinishedVideoMediaItemView({gameId, eventId, item} : {gameId: string, eventId: string, item: VideoMediaItemViewModel}) {
  const {t} = useTranslation("liveblog");
  const env = useEnvironment();
  const [accessToken, setAccessToken] = useState<string|undefined>(undefined);
  useEffect(() => {
    let active = true;
    env.accessToken().then(token => {
      if (active) {
        setAccessToken(token);
      }
    });
    return () => {
      active = false;
    };
  }, [env]);
  const opts: VideoFrameOptions = {
    notrack: "true",
  }
  if(accessToken){
    opts.access_token = accessToken;
  }
  if(!opts.access_token && env.clientId){
    opts.client_id = env.clientId;
  }
  const frameUrl = videoFrameUrl(gameId, eventId, item.id, opts)
  const fullFrameUrl = expandImageUrl(env.apiHost, frameUrl)

  const ratio = item.originalHeight/item.originalWidth;
  const cssHeight = `min(${ratio * 100}%, ${item.originalHeight}px,var(--t4-media-max-h))`

  return <MediaItemContainer className="tik4-media-item--video" item={item}>
    <div className="tik4-media-video">
      <div className="tik4-media-video__video-wrapper" style={{paddingTop: cssHeight, position: "relative", width: `calc(${cssHeight} * ${1/ratio})`, maxWidth: "100%"}}>
        <iframe src={fullFrameUrl} title={item.title || t('liveblog.media.video.fallback_title')} width="100%" height="100%" allowFullScreen={true} style={{position: "absolute", top: 0, left: 0}} />
      </div>
      <MediaItemTitleCredit item={item} />
    </div>

  </MediaItemContainer>

}

function MediaItemTitleCredit({item} : {item: ImageMediaItemViewModel | VideoMediaItemViewModel}) {
  if (item.title || item.credit) {
    return <div className="tik4-media-body">
      {item.title && <span className="tik4-media-body__title">{item.title}</span>}
      {/* Add punctuation if not in title and credit available */}
      {item.title && !item.title.match(/[.;!?,:]$/) && item.credit && <span className="tik4-media-body__punctuation">.</span>}
      {/* Add space */}
      {item.title && item.credit && ' '}
      {item.credit && <span className="tik4-media-body__credit">{item.credit}</span>}
    </div>
  } else {
    return null
  }
}

function VideoMediaItemView({gameId, eventId, item} : {gameId: string, eventId: string, item: VideoMediaItemViewModel}) {
  // Only a content-stream preview renders without a game id; there the media url is still the feed's own
  // file (whatever its extension), and the player frame could not address it anyway. A posted media —
  // including an externally hosted mp4 promoted via 'external-url' — always has a game id and keeps the frame.
  if(item.media?.needs_upload_from_device) {
    return <UnfinishedVideoMediaItemView item={item}/>
  }

  if(!gameId) {
    // Content-stream preview: no player frame or poster can exist yet, so nothing derived from the feed URL
    // may be used as an image. Play an https source inline; anything else (http, unknown) shows the placeholder.
    const preview = {...item, previews: pendingVideoPreviews()}
    return isHttpsUrl(item.media?.url)
      ? <MediaWithContentWarningView item={preview}>
          <RawVideoMediaItemView item={preview} url={item.media!.url!} />
        </MediaWithContentWarningView>
      : <UnfinishedVideoMediaItemView item={preview}/>
  }

  return <MediaWithContentWarningView item={item}>
    <FinishedVideoMediaItemView gameId={gameId} eventId={eventId} item={item} />
  </MediaWithContentWarningView>
}

function MediaErrorMessage({asset}: {asset: string|undefined}) {
  const {t} = useTranslation("liveblog");

  if(asset?.startsWith('error:ImageNotSupported')) {
    return <>{t('liveblog.media.error.unsupported_media_format')}</>
  } else if(asset && asset.startsWith('error')) {
    return <>{t('liveblog.media.error.generic', {code: asset})}</>
  } else {
    return <>{t('liveblog.media.error.generic', {code: 'unknown'})}</>
  }
}

interface MediaItemViewProps {
  gameId: string
  eventId: string
  item: MediaItemViewModel
  onMediaClick?: OnMediaClickHandler
}

export function MediaItemView({gameId, eventId, item, onMediaClick} : MediaItemViewProps) {
  if(item.media?.needs_upload_from_device !== 'promote_failed') {
    switch (item.kind) {
      case "image":
        return <MediaWithContentWarningView item={item}>
          <ImageMediaItemView item={item} onMediaClick={onMediaClick}/>
        </MediaWithContentWarningView>
      case "video":
        return <VideoMediaItemView gameId={gameId} eventId={eventId} item={item}/>
      default:
        return unknownKind(item);
    }
  } else {
    return <div className="tik4-media-item-error">
      <div className="tik4-media-item-error__container">
        <Frown className="tik4-media-item-error__icon tik4-icon" />
        <span className="tik4-media-item-error__text"><MediaErrorMessage asset={item.media?.asset} /></span>
      </div>
    </div>
  }
}
